import type { ReactNode } from "react";
import { Menu, MenuButton, MenuItem, MenuItems } from "@headlessui/react";
import { tv } from "tailwind-variants";

import { icons } from "../icons";
import { Tooltip } from "../tooltip";

const contextualMenuVariants = tv({
  slots: {
    trigger: [
      "inline-flex w-full cursor-pointer items-center justify-center gap-2 rounded-lg border px-4 py-2",
      "border-line-control bg-surface text-sm font-medium text-content shadow-raised",
      "transition-colors duration-150 hover:border-content-subtle hover:bg-surface-hover",
      "focus:outline-none data-focus-visible:ring-2 data-focus-visible:ring-focus-ring data-focus-visible:ring-offset-2 data-focus-visible:ring-offset-canvas",
    ],
    items: [
      "absolute right-0 z-50 mt-2 w-56 origin-top-right rounded-xl p-1",
      "border border-line bg-surface-raised shadow-popover focus:outline-none",
      "transition duration-150 ease-out data-closed:scale-95 data-closed:opacity-0",
    ],
    /* `group` lets each action's icon respond to the row's focus state. */
    item: [
      "group flex w-full cursor-pointer items-center justify-start gap-2.5 rounded-lg px-2.5 py-2 text-left text-sm text-content",
      "transition-colors duration-100",
      "data-focus:bg-surface-hover data-focus:text-content-strong",
      "data-disabled:cursor-not-allowed data-disabled:text-content-subtle data-disabled:data-focus:bg-transparent",
    ],
  },
});

const { item, items, trigger } = contextualMenuVariants();

export type ContextualMenuAction = {
  label: ReactNode;
  onClick: () => void;
  disabled?: boolean;
  disabledReason?: string;
};

type ContextualMenuProps = {
  actions: ContextualMenuAction[];
};

/*
  Built on Headless UI's Menu rather than a bare useState toggle: the previous
  version exposed role="menu" over plain buttons with no menuitem roles, no
  arrow-key or Escape handling, no focus return, and no dismiss-on-outside-click.
*/
export const ContextualMenu = ({ actions }: ContextualMenuProps) => {
  return (
    <Menu as="div" className="relative">
      <MenuButton className={trigger()}>
        Options
        <icons.ChevronDownIcon aria-hidden="true" className="size-4 text-content-muted" />
      </MenuButton>

      <MenuItems className={items()} transition>
        {actions.map((action, index) => {
          return (
            <Tooltip
              className="flex w-full"
              content={action.disabledReason ?? ""}
              disabled={!action.disabled || !action.disabledReason}
              key={index}
            >
              <MenuItem disabled={action.disabled}>
                <button className={item()} onClick={action.onClick} type="button">
                  {action.label}
                </button>
              </MenuItem>
            </Tooltip>
          );
        })}
      </MenuItems>
    </Menu>
  );
};
